/**
 * API error taxonomy.
 *
 * Messages come from the server and are written for users. The frontend never
 * invents a message for a server error, and never shows a raw stack.
 *
 * Reference: docs/09-api/01-api-conventions.md
 */

export interface ApiErrorBody {
  error: {
    code: string
    message: string
    details?: Record<string, unknown>
    request_id: string
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  static fromResponse(status: number, body: unknown): ApiError {
    const parsed = body as Partial<ApiErrorBody>
    const e = parsed?.error
    if (e?.code && e?.message) {
      return new ApiError(status, e.code, e.message, e.details, e.request_id)
    }
    return new ApiError(
      status,
      'internal_error',
      'Something went wrong. Please try again.',
    )
  }

  /** Would retrying with the same request plausibly succeed? */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429
  }

  get isAuthError(): boolean {
    return this.status === 401
  }

  get isPermissionError(): boolean {
    return this.status === 403
  }

  /** A plan limit was hit — the UI shows usage and an upgrade path, not an error. */
  get isPlanLimit(): boolean {
    return this.status === 402
  }

  get retryAfterSeconds(): number | undefined {
    const v = this.details?.['retry_after']
    return typeof v === 'number' ? v : undefined
  }
}

export class NetworkError extends Error {
  constructor(message = 'Could not reach the server. Check your connection.') {
    super(message)
    this.name = 'NetworkError'
  }
}
